import Reflux from 'reflux';
import { TaskActions, AppActions } from 'actions';
import SteelCompass from 'services/SteelCompass';
import debounce from 'oxygen-debounce';
import { history } from 'helpers';

const TaskStore = Reflux.createStore({
  listenables: [TaskActions],
  data: {
    filterMode: localStorage.getItem('taskFilterMode') || 'simple',
    tasks: {
      list: [],
      entities: {},
      filter: {
        userId: null,
        courseId: null,
        circleId: null,
        corrected: false
      },
      loading: true
    },
    students: {
      list: [],
      expanded: true,
      entities: {},
      filter: null,
      loading: true
    },
    courses: {
      list: [],
      expanded: true,
      entities: {},
      filter: null,
      loading: true
    },
    groups: {
      list: [],
      expanded: true,
      entities: {},
      filter: null,
      loading: true
    }
  },

  getInitialState() {
    return this.data;
  },

  loading(listName = 'tasks', loading = true) {
    this.data = {
      ...this.data,
      [listName]: {
        ...this.data[listName],
        loading
      }
    };
    this.trigger(this.data);
  },

  start({ courseId, userId, taskId }) {
    this.data = {
      ...this.data,
      tasks: {
        ...this.data.tasks,
        filter: {
          ...this.data.tasks.filter,
          userId: Number(userId) || null,
          courseId: Number(courseId) || null,
          taskId: Number(taskId) || null,
          circleId: null
        }
      }
    };
    this.trigger(this.data);
    TaskActions.readTasks();
    TaskActions.findTaskStudents();
    TaskActions.findTaskCourses();
    // TaskActions.findTaskGroups();
  },

  readTasks: debounce(function readTasksDebounced() {
    this.loading();
    const { filter } = this.data.tasks;
    const { corrected, ...other } = filter; // eslint-disable-line no-unused-vars
    if (filter.courseId || filter.userId || filter.groupId) {
      return SteelCompass.request('task', 'read', { ...other }).then(
        tasks => {
          TaskActions.readTasksComplete(tasks);
        },
        error => {
          this.loading('tasks', false);
          AppActions.genericError(error);
        }
      );
    }
  }, 500),

  findTaskStudents() {
    this.loading('students');
    return SteelCompass.request('coursio-ui', 'populateColumn2InTasks', {
      showUsersHavingFeedback: true
    }).then(
      students => {
        TaskActions.findTaskStudentsComplete(students);
      },
      error => {
        AppActions.genericError(error);
      }
    );
  },

  toggleFilterMode() {
    const filterMode =
      this.data.filterMode === 'simple' ? 'extended' : 'simple';
    this.data = {
      ...this.data,
      filterMode
    };
    localStorage.setItem('taskFilterMode', filterMode);
    this.trigger(this.data);
  },

  removeUploaded(id) {
    return SteelCompass.request('task-result', 'removeUploaded', { id }).then(
      res => {
        if (res) {
          AppActions.translatableMessage('tasks.fileRemoved');
        } else {
          AppActions.translatableMessage('tasks.fileNotRemoved');
        }
      },
      error => {
        AppActions.genericError(error);
      }
    );
  },

  findTaskGroups() {
    this.loading('groups');
    return SteelCompass.request('circle', 'findBuildable', {}).then(
      groups => {
        TaskActions.findTaskGroupsComplete(groups);
      },
      error => {
        this.loading('groups', false);
        AppActions.genericError(error);
      }
    );
  },

  findTaskCourses() {
    this.loading('courses');
    return SteelCompass.request('course', 'findBuildable', {
      showUncorrected: true
    }).then(
      courses => {
        TaskActions.findTaskCoursesComplete(courses);
      },
      error => {
        this.loading('courses', false);
        AppActions.genericError(error);
      }
    );
  },

  getEntities(input) {
    const list = [];
    const entities = {};
    input.forEach(item => {
      list.push(item.id);
      entities[item.id] = item;
    });
    return {
      list,
      entities
    };
  },

  findTaskStudentsComplete(list) {
    this.data = {
      ...this.data,
      students: {
        ...this.data.students,
        loading: false,
        ...this.getEntities(list)
      }
    };
    this.trigger(this.data);
  },

  toggleExpand(list) {
    this.data = {
      ...this.data,
      [list]: {
        ...this.data[list],
        expanded: !this.data[list].expanded
      }
    };
    this.trigger(this.data);
  },

  findTaskCoursesComplete(list) {
    this.data = {
      ...this.data,
      courses: {
        ...this.data.courses,
        loading: false,
        ...this.getEntities(list)
      }
    };
    this.trigger(this.data);
  },

  findTaskGroupsComplete(list) {
    this.data = {
      ...this.data,
      groups: {
        ...this.data.groups,
        loading: false,
        ...this.getEntities(list)
      }
    };
    this.trigger(this.data);
  },

  readTasksComplete(entities) {
    this.data = {
      ...this.data,
      tasks: {
        ...this.data.tasks,
        list: Object.keys(entities),
        entities,
        loading: false
      },
      entities: {}
    };
    this.trigger(this.data);
  },

  clearTaskSelection() {
    if (!this.data.tasks || !this.data.tasks.filter) {
      return;
    }
    this.data.tasks.filter.taskId = null;
    history.replace('/tasks');
  },

  toggleCorrected() {
    const { tasks } = this.data;
    this.data = {
      ...this.data,
      tasks: {
        ...tasks,
        filter: {
          ...tasks.filter,
          corrected: !tasks.filter.corrected
        }
      }
    };
    this.trigger(this.data);
  },

  setTaskFilter(filter) {
    const { tasks } = this.data;
    this.data = {
      ...this.data,
      tasks: {
        ...tasks,
        filter: {
          userId: null,
          courseId: null,
          circleId: null,
          ...filter
        }
      }
    };
    this.trigger(this.data);
    TaskActions.readTasks();
  },

  setGroupFilter(filter) {
    const { groups } = this.data;
    this.data = {
      ...this.data,
      groups: {
        ...groups,
        filter
      }
    };
    this.trigger(this.data);
  },

  setStudentFilter(filter) {
    const { students } = this.data;
    this.data = {
      ...this.data,
      students: {
        ...students,
        filter
      }
    };
    this.trigger(this.data);
  },

  setCourseFilter(filter) {
    const { courses } = this.data;
    this.data = {
      ...this.data,
      courses: {
        ...courses,
        filter
      }
    };
    this.trigger(this.data);
  },

  assessTask(taskId, taskResultId, feedback, grade, bodyFiles = [], silent) {
    return SteelCompass.request('task-result', 'assess', {
      id: taskResultId,
      grade,
      silent,
      text: feedback + bodyFiles.map(file => `[file:${file.id}]`).join('')
    }).then(
      response => {
        TaskActions.assessTaskComplete(
          taskId,
          taskResultId,
          feedback,
          grade,
          bodyFiles
        );
      },
      error => {
        AppActions.genericError(error);
      }
    );
  },

  assessTaskComplete(taskId, taskResultId, text, grade, bodyFiles = []) {
    const task = this.data.tasks.entities[taskId];
    this.data = {
      ...this.data,
      tasks: {
        ...this.data.tasks,
        entities: {
          ...this.data.tasks.entities,
          [taskId]: {
            ...task,
            taskResults: task.taskResults.map(taskResult => {
              if (taskResult.id === taskResultId) {
                return {
                  ...taskResult,
                  bodyFiles,
                  feedback: {
                    grade,
                    text
                  }
                };
              }
              return taskResult;
            })
          }
        }
      }
    };
    this.trigger(this.data);
    AppActions.translatableMessage('tasks.feedbackSent');
  }
});

export default TaskStore;



// WEBPACK FOOTER //
// ./src/js/stores/Task.js